import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as DeliveredIcon,
  LocalShipping as ShippingIcon,
  Inventory as ProcessingIcon,
  Cancel as CancelledIcon,
  Receipt as ReceiptIcon,
  PriorityHigh as PendingIcon,
} from '@mui/icons-material';
import { OrderResponse } from '../../hooks/useOrders';

interface UserOrdersTableProps {
  orders: OrderResponse[];
}

const UserOrdersTable: React.FC<UserOrdersTableProps> = ({ orders }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Function to handle view order
  const handleViewOrder = (orderId: number | string) => {
    navigate(`/admin/orders/${orderId}`);
  };
  
  // Function to get status chip styling
  const getStatusChipProps = (order: OrderResponse) => {
    // First check the new status fields, fall back to legacy status
    const status = order.order_status || order.status || 'processing';
    const shipmentStatus = order.shipment_status;
    
    if (shipmentStatus === 'delivered' || status === 'delivered') {
      return { 
        color: '#4caf50', 
        bg: alpha('#4caf50', 0.1),
        icon: <DeliveredIcon fontSize="small" />,
        label: 'Delivered'
      };
    } else if (shipmentStatus === 'in_transit' || shipmentStatus === 'out_for_delivery' || status === 'shipped') {
      return { 
        color: '#9c27b0', 
        bg: alpha('#9c27b0', 0.1),
        icon: <ShippingIcon fontSize="small" />,
        label: 'Shipped'
      };
    } else if (status === 'processing' || shipmentStatus === 'scheduled_for_pickup' || shipmentStatus === 'ready') {
      return { 
        color: '#2196f3', 
        bg: alpha('#2196f3', 0.1),
        icon: <ProcessingIcon fontSize="small" />,
        label: 'Processing'
      };
    } else if (status === 'pending' || status === 'draft' || shipmentStatus === 'pending') {
      return { 
        color: '#ff9800', 
        bg: alpha('#ff9800', 0.1),
        icon: <PendingIcon fontSize="small" />,
        label: 'Pending'
      };
    } else if (status === 'cancelled' || shipmentStatus === 'cancelled') {
      return { 
        color: '#f44336', 
        bg: alpha('#f44336', 0.1),
        icon: <CancelledIcon fontSize="small" />,
        label: 'Cancelled'
      };
    } else {
      return { 
        color: theme.palette.text.secondary, 
        bg: theme.palette.divider,
        icon: <ReceiptIcon fontSize="small" />,
        label: status.charAt(0).toUpperCase() + status.slice(1)
      };
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Table>
      <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Shipping</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {orders.map((order) => {
          const statusInfo = getStatusChipProps(order);
          const orderNumber = order.order_number || order.orderNumber || `Order ${order.id}`;
          const trackingNumber = order.tracking_number || order.trackingNumber;
          const totalPrice = order.total_price || order.totalPrice || 0;
          const createdAt = order.created_at || order.createdAt || '';
          
          return (
            <TableRow key={order.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {orderNumber}
                </Typography>
                {trackingNumber && (
                  <Typography variant="caption" color="text.secondary">
                    Tracking: {trackingNumber}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  icon={<ShippingIcon fontSize="small" />}
                  label={order.service || "Standard"}
                  sx={{
                    fontWeight: 500,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    borderRadius: 1.5,
                    py: 0.5,
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalPrice)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  icon={statusInfo.icon}
                  label={statusInfo.label}
                  sx={{
                    fontWeight: 600,
                    bgcolor: statusInfo.bg,
                    color: statusInfo.color,
                    borderRadius: 1.5,
                    py: 0.5,
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="View Order Details">
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewOrder(order.id)}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default UserOrdersTable; 